import { create } from 'zustand';
import { readPref, writePref } from '@/lib/storage';
import { getFreshAccessToken } from '@/lib/authToken';
import { fetchWithTimeout, reasonOf } from '@/lib/http';
import config from '@/lib/config';

/**
 * Проект UNIA = рабочая область пользователя. В CRM он уходит заголовком
 * X-Project-Id, а кабинет Chatwoot — это chatwoot_account_id проекта.
 * Список отдаёт ГиперТим (GET /api/projects), как и в оболочке GiperUI
 * (GiperUI/src/store/projectStore.ts → fetchProjects).
 */
export interface Project {
  id: string;
  name: string;
  org_id: string;
  is_personal?: boolean;
  /** null — кабинет Переписок для проекта ещё не заведён. */
  chatwoot_account_id?: number | null;
}

export const ACTIVE_PROJECT_KEY = 'saleus-active-project-id';

/**
 * Выбор активного проекта: сохранённый, если он ещё в списке; иначе первый.
 * 🔒 Сохранённый id, которого нет в списке пользователя, не доверяем никогда
 * (чужой/устаревший id на общем устройстве) — как и в GiperUI.
 */
export function pickActiveProject(projects: Project[], saved: string | null): string {
  if (saved && projects.some((p) => p.id === saved)) return saved;
  return projects[0]?.id ?? '';
}

type Статус = 'idle' | 'loading' | 'ready' | 'error';

interface ProjectState {
  projects: Project[];
  activeProjectId: string;
  status: Статус;
  error: string | null;
  load: () => Promise<void>;
  setActive: (id: string) => void;
  reset: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  activeProjectId: '',
  status: 'idle',
  error: null,

  load: async () => {
    if (get().status === 'loading') return;
    set({ status: 'loading', error: null });
    try {
      const token = await getFreshAccessToken();
      if (!token) throw new Error('Нет действующего входа');
      const res = await fetchWithTimeout(`${config.TEAM_API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await reasonOf(res));
      const body: unknown = await res.json();
      if (!Array.isArray(body)) throw new Error('Сервер вернул список проектов в неожиданном виде');
      // Личное пространство в выборе проектов не показываем никогда.
      const projects = (body as Project[]).filter((p) => !p.is_personal);
      const saved = await readPref(ACTIVE_PROJECT_KEY);
      const active = pickActiveProject(projects, get().activeProjectId || saved);
      set({ projects, activeProjectId: active, status: 'ready' });
      if (active && active !== saved) void writePref(ACTIVE_PROJECT_KEY, active);
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : 'Не удалось загрузить проекты' });
    }
  },

  setActive: (id) => {
    if (!get().projects.some((p) => p.id === id)) return;
    set({ activeProjectId: id });
    void writePref(ACTIVE_PROJECT_KEY, id);
  },

  reset: () => {
    set({ projects: [], activeProjectId: '', status: 'idle', error: null });
    void writePref(ACTIVE_PROJECT_KEY, null);
  },
}));
